//import * as XLSX from "xlsx";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { formatCurrency } from "../../CommonUtils/currencyUtils";
import { computeCreditTypeTotals } from "../../CommonUtils/taxCreditTotals";

export const CATEGORY_EXPORT_COLUMNS = [
  {
    key: "location",
    label: "Location",
    aliases: ["location"],
    type: "state",
  },
  {
    key: "jobCreation",
    label: "Job Creation",
    aliases: ["job creation", "jobs"],
    type: "state",
  },
  {
    key: "industry",
    label: "Industry",
    aliases: ["industry"],
    type: "state",
  },
  {
    key: "fez",
    label: "FEZ",
    aliases: ["fez", "federal empowerment zone"],
    type: "federal",
  },
  {
    key: "wotc",
    label: "WOTC",
    aliases: [
      "wotc",
      "work opportunity tax credit",
      "work opportunity tax credits",
    ],
    type: "federal",
  },
  {
    key: "fica",
    label: "FICA",
    aliases: [
      "fica",
      "fica extra",
      "fica tip tax credit",
      "fica tip tax credits",
    ],
    type: "federal",
  },
  {
    key: "capitalInv",
    label: "Capital Inv",
    aliases: ["capital inv", "capital investment", "capital investments"],
    type: "state",
  },
  {
    key: "payroll",
    label: "Payroll",
    aliases: ["payroll"],
    type: "state",
  },
  {
    key: "other",
    label: "Other",
    aliases: ["other", "misc"],
    type: "state",
  },
];

const normalizeCategoryAlias = (value) =>
  typeof value === "string"
    ? value.trim().toLowerCase().replace(/[^a-z0-9]/g, "")
    : "";

const CATEGORY_ALIAS_LOOKUP = CATEGORY_EXPORT_COLUMNS.reduce(
  (acc, column) => {
    const variants = [column.label, ...(column.aliases || [])];
    variants.forEach((variant) => {
      const normalized = normalizeCategoryAlias(variant);
      if (normalized && !acc[normalized]) {
        acc[normalized] = column.key;
      }
    });
    return acc;
  },
  {}
);

export const CATEGORY_DEFAULT_KEY = "other";

export const resolveCategoryColumnKey = (value) => {
  const normalized = normalizeCategoryAlias(value);
  if (!normalized) return null;
  return CATEGORY_ALIAS_LOOKUP[normalized] || null;
};

export const initializeCategoryTotals = () =>
  CATEGORY_EXPORT_COLUMNS.reduce((acc, column) => {
    acc[column.key] = 0;
    return acc;
  }, {});

const CREDIT_AMOUNT_FIELDS = [
  "projectedTaxCreditAmount",
  "estimatedCreditAmount",
  "estimatedAmount",
  "projectedAmount",
  "creditAmount",
  "amount",
  "value",
];

const parseCreditAmountValue = (value) => {
  if (value === null || typeof value === "undefined") return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string") {
    const cleaned = value.replace(/[^0-9.-]+/g, "");
    if (!cleaned) return null;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const getCreditAmountFromEntry = (entry = {}) => {
  for (const field of CREDIT_AMOUNT_FIELDS) {
    if (
      Object.prototype.hasOwnProperty.call(entry, field) &&
      entry[field] !== null &&
      typeof entry[field] !== "undefined"
    ) {
      const parsed = parseCreditAmountValue(entry[field]);
      if (parsed !== null) {
        return parsed;
      }
    }
  }
  return null;
};

const pickFieldValue = (source, fieldNames = []) => {
  if (!source || !Array.isArray(fieldNames)) return undefined;
  for (const field of fieldNames) {
    if (
      field &&
      Object.prototype.hasOwnProperty.call(source, field) &&
      source[field] !== undefined &&
      source[field] !== null
    ) {
      const value = source[field];
      if (String(value).trim() !== "") {
        return value;
      }
    }
  }
  return undefined;
};

const resolveFipsDisplay = (source, fieldNames = []) => {
  const value = pickFieldValue(source, fieldNames);
  if (value === undefined) {
    return "No Data Available";
  }
  return value;
};

const encodeToBase64 = (value) => {
  try {
    if (typeof window !== "undefined" && typeof window.btoa === "function") {
      return window.btoa(unescape(encodeURIComponent(value)));
    }
    if (typeof Buffer !== "undefined") {
      return Buffer.from(value, "utf-8").toString("base64");
    }
  } catch (err) {
    console.warn("Failed to encode payload:", err);
  }
  return "";
};

const getExcelArgumentSeparator = () => {
  try {
    const parts = new Intl.NumberFormat().formatToParts(1.1);
    const decimalPart = parts.find((part) => part.type === "decimal");
    return decimalPart?.value === "," ? ";" : ",";
  } catch (err) {
    console.warn("Failed to resolve argument separator:", err);
    return ",";
  }
};

const hyperlinkFormulaRegex = /=HYPERLINK\("([^"]+)"[,;]"([^"]+)"\)/i;

const parseHyperlinkFormula = (value) => {
  if (typeof value !== "string") return null;
  const match = value.match(hyperlinkFormulaRegex);
  if (!match) return null;
  const decode = (text) => text.replace(/""/g, '"');
  return {
    target: decode(match[1]),
    label: decode(match[2]),
  };
};

const formatCreditLabel = (entry) => {
  if (!entry) return "";
  if (typeof entry === "string") {
    return entry.trim();
  }
  if (typeof entry === "object") {
    return (
      entry.name ||
      entry.creditName ||
      entry.description ||
      ""
    );
  }
  return "";
};

const buildCreditDetailLink = (taxCredit, summary) => {
  const origin =
    typeof window !== "undefined" && window.location
      ? window.location.origin
      : "";
  const identifier =
    taxCredit?.prospectBusinessId ||
    taxCredit?.businessId ||
    taxCredit?.id ||
    "";
  if (!origin || !identifier) return "";
  const payload = {
    businessName: summary.businessName,
    address: summary.address,
    employees: summary.employees,
    annualHires: summary.annualHires,
    projectedCredit: summary.projectedCredit,
  };
  if (
    typeof summary.annualHiresNumeric === "number" &&
    !Number.isNaN(summary.annualHiresNumeric)
  ) {
    payload.annualHiresNumeric = summary.annualHiresNumeric;
  }
  const encodedPayload = encodeToBase64(JSON.stringify(payload));
  const query = encodedPayload
    ? `?payload=${encodeURIComponent(encodedPayload)}`
    : "";
  return `${origin}/credit-details/${encodeURIComponent(identifier)}${query}`;
};

const resolveNumber = (value) => {
  if (typeof value === "number" && !Number.isNaN(value)) {
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeCategoryLabel = (value) => {
  if (typeof value === "string") {
    const normalized = value.trim();
    if (!normalized) return "";
    if (normalized.toLowerCase() === "no data available") return "";
    return normalized;
  }
  if (typeof value === "number") return String(value).trim();
  return "";
};

const sanitizeVersionToken = (value) => {
  if (typeof value !== "string") return "";
  return value
    .replace(/[\\/:*?"<>|]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\s+/g, "_");
};

const normalizeCreditEntry = (entry) => {
  if (!entry) return null;
  if (typeof entry === "string") {
    const label = entry.trim();
    return label ? { name: label } : null;
  }
  if (typeof entry === "object") {
    if (entry.name) return entry;
    if (entry.creditName) {
      return { ...entry, name: entry.creditName };
    }
  }
  return null;
};

const normalizeCreditEntries = (entries = []) =>
  (Array.isArray(entries) ? entries : [])
    .map((entry) => normalizeCreditEntry(entry))
    .filter(Boolean);

const resolveExportCredits = (taxCredit = {}) => {
  const primary = normalizeCreditEntries(
    taxCredit.taxCredits?.[0]?.availableTaxCredits
  );
  if (primary.length) return primary;
  const fromAvailable = normalizeCreditEntries(taxCredit.availableCredits);
  if (fromAvailable.length) return fromAvailable;
  return normalizeCreditEntries(taxCredit.eligibleCredits);
};

export const convertJsonToExcel = (jsonData, fileName, options = {}) => {
  const headingText =
    options?.heading && String(options.heading).trim()
      ? String(options.heading).trim()
      : null;
  const worksheetData = jsonData.map((row) => {
    if (Array.isArray(row["Credits identified"])) {
      const creditsOnly = row["Credits identified"]
        .map((entry) => {
          if (typeof entry === "string") return entry.trim();
          if (entry && typeof entry === "object") {
            return entry.name || "";
          }
          return "";
        })
        .filter(Boolean)
        .join("\n");
      return {
        ...row,
        "Credits identified": creditsOnly || "No Data Available",
      };
    }
    return {
      ...row,
    };
  });

  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet([]);
  const dataOrigin = headingText ? "A3" : "A1";
  if (headingText) {
    XLSX.utils.sheet_add_aoa(worksheet, [[headingText]], { origin: "A1" });
    XLSX.utils.sheet_add_aoa(worksheet, [[]], { origin: "A2" });
  }
  XLSX.utils.sheet_add_json(worksheet, worksheetData, {
    origin: dataOrigin,
    skipHeader: false,
  });
  const sheetRef = worksheet["!ref"];
  const range = sheetRef ? XLSX.utils.decode_range(sheetRef) : null;

  if (headingText && worksheet["A1"]) {
    const lastColumnIndex =
      typeof range?.e?.c === "number" ? range.e.c : 0;
    worksheet["A1"].s = {
      font: { bold: true, sz: 14, color: { rgb: "333333" } },
      fill: { fgColor: { rgb: "FFF4E5" } },
      alignment: { horizontal: "center", vertical: "center" },
    };
    const merges = worksheet["!merges"] || [];
    merges.push({
      s: { r: 0, c: 0 },
      e: { r: 0, c: Math.max(0, lastColumnIndex) },
    });
    worksheet["!merges"] = merges;
    worksheet["!rows"] = worksheet["!rows"] || [];
    worksheet["!rows"][0] = { hpt: 28 };
  }

  if (range) {
    for (let rowNum = range.s.r; rowNum <= range.e.r; rowNum++) {
      for (let colNum = range.s.c; colNum <= range.e.c; colNum++) {
        const cellAddress = XLSX.utils.encode_cell({ r: rowNum, c: colNum });
        if (!worksheet[cellAddress]) continue;
        if (colNum === range.s.c && rowNum > range.s.r) {
          const cellValue = worksheet[cellAddress].v;
          const parsedHyperlink = parseHyperlinkFormula(cellValue);

          if (parsedHyperlink) {
            worksheet[cellAddress] = {
              v: parsedHyperlink.label,
              l: { Target: parsedHyperlink.target },
              s: { font: { color: { rgb: "0000FF" }, underline: true } },
            };
          } else {
            worksheet[cellAddress].s = {
              font: { color: { rgb: "0000FF" }, underline: true },
            };
          }
        }
      }
    }
  }

  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
    cellStyles: true,
  });
  const data = new Blob([excelBuffer], { type: "application/octet-stream" });
  saveAs(data, `${fileName}.xlsx`);
};

export const prepareRequiredJsonToExcelDownload = (selectedTaxcredits = []) => {
  return selectedTaxcredits.map((taxCredit) => {
    const credits = resolveExportCredits(taxCredit);
    const creditsText = credits.length
      ? credits
          .map((item) => formatCreditLabel(item))
          .filter(Boolean)
          .join(" | ")
      : "No Data Available";
    const employeesNumeric = resolveNumber(taxCredit?.locationEmployeesCount);
    const formattedEmployees =
      typeof employeesNumeric === "number"
        ? formatCurrency(employeesNumeric)
        : "No Data Available";
    const annualHiresNumeric = resolveNumber(taxCredit?.annualHires);
    const formattedAnnualHires =
      typeof annualHiresNumeric === "number"
        ? formatCurrency(annualHiresNumeric)
        : "No Data Available";
    const projectedCreditValue = resolveNumber(
      taxCredit.taxCredits?.[0]?.totalProjectedTaxCreditAmount ??
        taxCredit.totalProjectedTaxCreditAmount ??
        taxCredit.projectedCredit ??
        taxCredit.projectedTaxCreditAmount
    );
    const projectedCredit =
      typeof projectedCreditValue === "number"
        ? `$${formatCurrency(projectedCreditValue)}`
        : "No Data Available";
    const creditTotals = computeCreditTypeTotals([taxCredit]);
    const formatBucketValue = (value) =>
      typeof value === "number" && value > 0 ? `$${formatCurrency(value)}` : 0;
    const address = [
      taxCredit.physicalAddress,
      taxCredit.physicalCity,
      taxCredit.state,
      taxCredit.physicalZip,
    ]
      .filter(Boolean)
      .join(", ");

    const summaryForLink = {
      businessName: taxCredit.businessName || "No Data Available",
      address,
      employees: formattedEmployees,
      annualHires: formattedAnnualHires,
      projectedCredit,
      annualHiresNumeric,
    };
    const detailUrl = buildCreditDetailLink(taxCredit, summaryForLink);
    const safeDisplayName = (taxCredit.businessName || "No Data Available").replace(/"/g, "\"\"");
    const argumentSeparator = getExcelArgumentSeparator();
    const nameCell =
      detailUrl && taxCredit.businessName
        ? `=HYPERLINK("${detailUrl}"${argumentSeparator}"${safeDisplayName}")`
        : safeDisplayName;

    const resolvedNaicsCode =
      taxCredit.preferredNaics ||
      taxCredit.naics ||
      taxCredit.naicS01 ||
      "No Data Available";

    const resolvedNaicsDescription =
      taxCredit.naicsDescription ||
      taxCredit.naicS01Description ||
      taxCredit.preferredNaicsDescription ||
      "No Data Available";

    const resolvedBusinessType =
      taxCredit.businessType ||
      taxCredit.preferredNaicsDescription ||
      taxCredit.naicsDescription ||
      taxCredit.naicS01Description ||
      taxCredit.siC01Description ||
      "No Data Available";
    const categorySet = new Set();
    credits.forEach((credit) => {
      const label = normalizeCategoryLabel(
        credit?.category || credit?.Category || ""
      );
      if (label) {
        categorySet.add(label);
      }
    });
    const creditCategoriesText =
      categorySet.size > 0
        ? Array.from(categorySet).join(" | ")
        : "No Data Available";

    const categoryTotals = initializeCategoryTotals();
    const assignCategoryAmount = (label, amount) => {
      if (typeof amount !== "number" || Number.isNaN(amount) || amount <= 0) {
        return;
      }
      const keyFromLabel = resolveCategoryColumnKey(label);
      if (keyFromLabel) {
        categoryTotals[keyFromLabel] += amount;
        return;
      }
      if (label) {
        categoryTotals[CATEGORY_DEFAULT_KEY] += amount;
      }
    };
    credits.forEach((credit) => {
      const amount = getCreditAmountFromEntry(credit);
      if (typeof amount !== "number" || amount <= 0) {
        return;
      }
      assignCategoryAmount(
        credit?.category || credit?.Category || "",
        amount
      );
    });
    const hasCategoryTotals = Object.values(categoryTotals).some(
      (value) => value > 0
    );
    if (
      !hasCategoryTotals &&
      taxCredit.precalculatedCategoryTotals &&
      typeof taxCredit.precalculatedCategoryTotals === "object"
    ) {
      Object.entries(taxCredit.precalculatedCategoryTotals).forEach(
        ([rawKey, rawValue]) => {
          const resolvedKey = resolveCategoryColumnKey(rawKey);
          if (!resolvedKey) return;
          const fallbackValue = parseCreditAmountValue(rawValue);
          if (typeof fallbackValue === "number" && fallbackValue > 0) {
            categoryTotals[resolvedKey] += fallbackValue;
          }
        }
      );
    }
    const categoryColumnValues = CATEGORY_EXPORT_COLUMNS.reduce(
      (acc, column) => {
        const value = categoryTotals[column.key] || 0;
        acc[column.label] =
          typeof value === "number" && value > 0
            ? `$${formatCurrency(value)}`
            : 0;
        return acc;
      },
      {}
    );

    return {
      Name: nameCell,
      "Business type": resolvedBusinessType,
      Website: taxCredit.url ? `https://${taxCredit.url}` : "",
      Address: address,
      Latitude: taxCredit.latitude || "No Data Available",
      Longitude: taxCredit.longitude || "No Data Available",
      "Empowerment zone": taxCredit.empowermentZone || "No Data Available",
      "Projected credit": projectedCredit,
      "Federal WOTC": formatBucketValue(creditTotals.federalWotc),
      "Federal Non-WOTC": formatBucketValue(creditTotals.federalNonWotc),
      "State credits": formatBucketValue(creditTotals.state),
      Employees: formattedEmployees,
      "Annual hires": formattedAnnualHires,
      "Census tract": taxCredit.censusTract || "No Data Available",
      "FIPS code": taxCredit.fipsCode || "No Data Available",
      "State FIPS": resolveFipsDisplay(taxCredit, [
        "stateFips",
        "stateFIPS",
        "StateFIPS",
        "StateFips",
      ]),
      "County FIPS": resolveFipsDisplay(taxCredit, [
        "countyFips",
        "countyFIPS",
        "CountyFIPS",
        "CountyFips",
      ]),
      "NAICS code": resolvedNaicsCode,
      "NAICS description": resolvedNaicsDescription,
      "Credits identified": creditsText,
      "Credit categories": creditCategoriesText,
      ...categoryColumnValues,
      "Prospect business ID": taxCredit.prospectBusinessId || "",
    };
  });
};

export const resolveVersionValue = (businesses = []) => {
  if (!Array.isArray(businesses)) return null;
  for (const item of businesses) {
    if (!item) continue;
    const raw =
      item.version ??
      item.Version ??
      (typeof item.searchVersion !== "undefined"
        ? item.searchVersion
        : null);
    if (raw !== null && typeof raw !== "undefined") {
      const trimmed = String(raw).trim();
      if (trimmed) {
        return trimmed;
      }
    }
  }
  return null;
};

export const resolveVersionHeading = (businesses = []) => {
  const versionValue = resolveVersionValue(businesses);
  return versionValue ? `Version: ${versionValue}` : null;
};

export const buildVersionedFileName = (baseName, businesses = []) => {
  const versionValue = resolveVersionValue(businesses);
  if (!versionValue) return baseName;
  const safeToken = sanitizeVersionToken(versionValue);
  if (!safeToken) return baseName;
  return `${baseName}_${safeToken}`;
};
